import { MouseEvent, PointerEvent, useEffect, useRef } from "react";

// holdDelay is how long a touch stays still before it's read as a touch and hold, in ms
const holdDelay = 500;

// holdDistance is how far the pointer can drift before the touch stops being a hold
const holdDistance = 8;

// useHold reads a touch and hold. A mouse is left out, since it has the right-click
// for the same thing. Spread `handlers` on the element, or call `start` and `cancel`
// from handlers of your own.
export function useHold(props: {
    disabled?: boolean;
    onHold?: (point: { x: number; y: number }) => void;
}) {
    const timer = useRef(0);
    const from = useRef<{ x: number; y: number } | null>(null);

    // held keeps the click that ends a hold from acting on the element
    const held = useRef(false);

    const cancel = () => {
        window.clearTimeout(timer.current);
        timer.current = 0;
        from.current = null;
    };

    useEffect(() => cancel, []);

    const start = (e: PointerEvent) => {
        held.current = false;

        const onHold = props.onHold;
        if (!onHold || props.disabled || e.button !== 0 || e.pointerType === "mouse") return;

        const point = { x: e.clientX, y: e.clientY };
        from.current = point;
        timer.current = window.setTimeout(() => {
            timer.current = 0;
            from.current = null;
            held.current = true;
            onHold(point);
        }, holdDelay);
    };

    return {
        start: start,
        cancel: cancel,

        // wasHold is true for the click that ends a hold
        wasHold: () => {
            const value = held.current;
            held.current = false;
            return value;
        },

        handlers: {
            onPointerDown: start,
            onPointerMove: (e: PointerEvent) => {
                const point = from.current;
                if (!point) return;

                if (Math.abs(e.clientX - point.x) > holdDistance || Math.abs(e.clientY - point.y) > holdDistance) cancel();
            },
            onPointerUp: cancel,
            onPointerCancel: cancel,
            onClickCapture: (e: MouseEvent) => {
                if (!held.current) return;

                held.current = false;
                e.stopPropagation();
                e.preventDefault();
            },
        },
    };
}
